import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const InputGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				className={cn(
					"flex w-full flex-col rounded-xl border border-input/85 bg-card/95 shadow-md transition-[border-color,box-shadow,background-color] duration-200 focus-within:border-accent-blue-border focus-within:bg-card focus-within:shadow-lg",
					className
				)}
				{...props}
			/>
		);
	}
);
InputGroup.displayName = "InputGroup";

interface InputGroupAddonProps extends React.HTMLAttributes<HTMLDivElement> {
	align?: "start" | "end" | "block-start" | "block-end";
}

const InputGroupAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
	({ className, align = "end", ...props }, ref) => {
		const alignmentClasses = {
			start: "items-start",
			end: "items-end",
			"block-start": "items-start",
			"block-end": "items-end",
		};

		return (
			<div
				ref={ref}
				className={cn(
					"inline-flex gap-2 bg-muted/35 px-2.5 py-2 dark:bg-input/45",
					alignmentClasses[align],
					className
				)}
				{...props}
			/>
		);
	}
);
InputGroupAddon.displayName = "InputGroupAddon";

const InputGroupText = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
	({ className, ...props }, ref) => {
		return <span ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />;
	}
);
InputGroupText.displayName = "InputGroupText";

const InputGroupButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
	({ className, ...props }, ref) => {
		return <Button ref={ref} className={cn("", className)} {...props} />;
	}
);
InputGroupButton.displayName = "InputGroupButton";

const InputGroupTextarea = React.forwardRef<
	HTMLTextAreaElement,
	React.ComponentProps<typeof Textarea>
>(({ className, ...props }, ref) => {
	return (
		<Textarea
			ref={ref}
			disableFocusStyle
			className={cn(
				"flex-1 resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-70 dark:bg-transparent",
				className
			)}
			{...props}
		/>
	);
});
InputGroupTextarea.displayName = "InputGroupTextarea";

const InputGroupInput = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>(
	({ className, ...props }, ref) => {
		return (
			<Input
				ref={ref}
				disableFocusStyle
				className={cn(
					"flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-70 dark:bg-transparent",
					className
				)}
				{...props}
			/>
		);
	}
);
InputGroupInput.displayName = "InputGroupInput";

export {
	InputGroup,
	InputGroupAddon,
	InputGroupText,
	InputGroupButton,
	InputGroupTextarea,
	InputGroupInput,
};
